import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AuthContext } from "@/App";
import AnimatedBackground from "@/components/AnimatedBackground";
import "./SentenceCorrectorGuide.css";

const SentenceCorrectorGuide = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const goHome = () => navigate("/");
  const goChecker = () => navigate("/checker");

  return (
    <div className="scg-page">
      <Helmet>
        <title>Complete Sentence Corrector Guide — Fix Any Sentence Online | GrammAA</title>
        <meta                                                                                                                                     
          name="description"
          content="Complete, long-form guide: how to correct any sentence online using GrammAA's AI Sentence Corrector. Detailed steps, grammar explanations, common mistakes, rewrite types, examples, and best practices."
        />
        <meta
          name="keywords"
          content="sentence corrector, fix my sentence, correct my sentence, sentence correction guide, sentence rewrites, grammar mistakes, improve sentence structure, online sentence checker, AI sentence corrector"
        />
        <meta property="og:title" content="Complete Sentence Corrector Guide — GrammAA" />
        <meta
          property="og:description"
          content="Learn how to correct sentences, fix grammar errors, choose rewrites, and improve clarity with GrammAA's AI sentence corrector. Step-by-step, examples, and learning tips."
        />
        <meta property="og:image" content="/icons/logo-512.png" />
        <meta property="og:url" content="https://grammaa.com/ai-sentence-corrector/how-to" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <div className="scg-bg" />
      <AnimatedBackground />

      <main className="scg-container">
        {/* HERO */}
        <section className="scg-hero glass-card">
          <div className="scg-hero-left">
            <h1 className="scg-title">
              Fix Any English Sentence - Ultimate Step-by-Step Guide
            </h1>

            <p className="scg-sub">
              This comprehensive guide walks you through every step: how to use a sentence corrector,
              how corrections are made, the grammar behind common fixes, rewrite strategies, and dozens of
              real before/after examples. When you’re ready, open the checker and correct a sentence in one click.
            </p>

            <div className="scg-ctas">
              <button className="btn btn-primary" onClick={goChecker}>
                Go to Checker
              </button>
              <button className="btn btn-ghost" onClick={goHome}>
                Home
              </button>
            </div>

            <p className="scg-note">
              Tip: for single-sentence precision paste one sentence; for context-aware suggestions paste a paragraph.
            </p>

            <div className="scg-hero-features">
              <div className="feat">
                <div className="icon">⚡</div>
                <div>
                  <strong>Fast results</strong>
                  <div className="muted">Instant corrections & rewrites</div>
                </div>
              </div>

              <div className="feat">
                <div className="icon">🧭</div>
                <div>
                  <strong>Context-aware</strong>
                  <div className="muted">Keeps your meaning intact</div>
                </div>
              </div>

              <div className="feat">
                <div className="icon">📚</div>
                <div>
                  <strong>Learn as you fix</strong>
                  <div className="muted">Explanations for each correction</div>
                </div>
              </div>
            </div>
          </div>

          <div className="scg-hero-right">
            <div className="screenshot-frame">
              <img src="/screenshots/s1-home.png" alt="Sentence Corrector start" />
              <div className="screenshot-caption">Start here: paste a sentence and click the checker.</div>
            </div>
          </div>
        </section>

        {/* ARTICLE (LONG, DETAILED) */}
        <article className="scg-article glass-card">
          {/* INTRO */}
          <section className="scg-section">
            <h2>Introduction - What this guide covers</h2>
            <p>
              This guide explains how to correct any English sentence using GrammAA’s Sentence Corrector, and importantly why each correction
              improves the sentence. You'll see real-world examples, learn common grammar rules, discover rewrite strategies,
              and learn how to choose the best version for professional, academic, or casual use.
            </p>
            <p>
              Use the checklist in each section to practise. When you’re ready, open the checker (<button className="small-link" onClick={goChecker}>GrammAA</button>) and test your own sentences.
            </p>
          </section>

          {/* HOW IT WORKS */}
          <section className="scg-section">
            <h2>How the Sentence Corrector Works (briefly)</h2>
            <p>
              The corrector analyzes syntax (word order), morphology (word forms like verb tense), punctuation, and semantics (meaning).
              It uses language models to propose corrections that are grammatically correct and natural-sounding.
              Corrections include:
            </p>
            <ul className="scg-bullets">
              <li>Subject–verb agreement fixes (he go → he goes)</li>
              <li>Tense normalization (I goed → I went)</li>
              <li>Punctuation fixes (commas, apostrophes, periods)</li>
              <li>Contractions and formal forms (do not → don't, depending on tone)</li>
              <li>Clarity and concision rewrites (remove redundant words)</li>
            </ul>

            <p>
              The tool also offers multiple rewrite options tailored for tone: <em>formal</em>, <em>concise</em>, <em>creative</em>, and <em>simple</em>.
            </p>
          </section>

          {/* DETAILED STEP-BY-STEP */}
          <section className="scg-section steps">
            <h2>Step-by-step: Use the Sentence Corrector</h2>

            <div className="step">
              <div className="step-index">1</div>
              <div className="step-body">
                <h3>Open the checker</h3>
                <p>
                  Visit the checker at: 
                  <button className="small-link" onClick={goChecker}>GrammAA</button>
                  - or click the "Go to Checker" button above. The checker preserves any text if you are redirected to log in.
                </p>
              </div>
            </div>

            <div className="step">
              <div className="step-index">2</div>
              <div className="step-body">
                <h3>Paste or type your sentence</h3>
                <p>
                  Enter exactly the sentence you want to correct. For sentence-focused corrections, keep it to one sentence.
                  For context-aware changes, include the previous sentence or paragraph.
                </p>

                <div className="scshot">
                  <img src="/screenshots/s2-input.png" alt="paste sentence" />
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-index">3</div>
              <div className="step-body">
                <h3>Click the check button</h3>
                <p>
                  The AI returns a corrected sentence, grammar highlights, and rewrites. If you reach a free-check limit, a quick signup will unlock more usage.
                </p>

                <div className="two-thumbs">
                  <div className="thumb-small">
                    <img src="/screenshots/s3-signup.png" alt="signup" />
                    <div className="thumb-caption">Quick signup if required</div>
                  </div>
                  <div className="thumb-small">
                    <img src="/screenshots/s4-dashboard.png" alt="dashboard" />
                    <div className="thumb-caption">Dashboard & saved history</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-index">4</div>
              <div className="step-body">
                <h3>Read the corrected sentence & alternatives</h3>
                <p>
                  The tool displays a correction and multiple rewrites. Pick the one matching your tone and intent.
                </p>
                <div className="scshot">
                  <img src="/screenshots/s5-result.png" alt="result" />
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-index">5</div>
              <div className="step-body">
                <h3>Open the mistakes panel</h3>
                <p>
                  The mistakes panel explains why each change was suggested - for instance, missing apostrophes, wrong verb forms, or misplaced commas.
                </p>
                <div className="scshot">
                  <img src="/screenshots/s6-mistakes.png" alt="mistakes" />
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-index">6</div>
              <div className="step-body">
                <h3>Try rewrites and choose tone</h3>
                <p>
                  Test different rewrite styles and use the one that matches your audience: formal for emails, short for social captions, creative for storytelling.
                </p>
                <div className="scshot">
                  <img src="/screenshots/s7-rewrites.png" alt="rewrites" />
                </div>
              </div>
            </div>
          </section>

          {/* IN-DEPTH GRAMMAR EXPLANATIONS */}
          <section className="scg-section">
            <h2>In-depth: Common grammar fixes and why they matter</h2>

            <h3>1. Subject–verb agreement</h3>
            <p>
              Rule: a singular subject needs a singular verb; a plural subject needs a plural verb.
              Example: "He run to the store" → "He runs to the store".
            </p>

            <h3>2. Tense consistency</h3>
            <p>
              Keep the verb tense consistent unless you intentionally shift time. Example:
              "She said she goes" is usually wrong; prefer "She said she would go" or "She said she goes every week" depending on context.
            </p>

            <h3>3. Articles (a, an, the)</h3>
            <p>
              Correct article use improves clarity: "I saw dog" → "I saw a dog" or "the dog" if specific.
            </p>

            <h3>4. Punctuation: commas & clauses</h3>
            <p>
              Commas separate clauses and help readability. "Before eating my friend left" → "Before eating, my friend left."
            </p>

            <h3>5. Contractions & tone</h3>
            <p>
              Choose contractions (don't, it's) for a conversational tone; expand them (do not, it is) for formal writing. GrammAA respects tone choices.
            </p>
          </section>

          {/* REWRITES & TONE */}
          <section className="scg-section">
            <h2>Rewrite strategies - pick the best tone</h2>
            <p>
              Common rewrite styles and when to use them:
            </p>
            <ul className="scg-bullets">
              <li><strong>Short & Clear:</strong> removes filler words. Great for headlines and SMS.</li>
              <li><strong>Formal & Professional:</strong> full words, polite phrasing. Use for emails, proposals, academic writing.</li>
              <li><strong>Simple:</strong> plain language, easy for non-native speakers.</li>
              <li><strong>Creative:</strong> expressive phrasing, used in storytelling or marketing copy.</li>
            </ul>
          </section>

          {/* EXAMPLES (many) */}
          <section className="scg-section examples">
            <h2>Extensive examples - before & after</h2>

            <div className="example-grid">
              <div className="example">
                <div className="label">Before</div>
                <div className="text">he dont know what is going on in the meeting</div>
                <div className="label">After</div>
                <div className="text result">He doesn’t know what is going on in the meeting.</div>
              </div>

              <div className="example">
                <div className="label">Before</div>
                <div className="text">i not understand what you trying say</div>
                <div className="label">After</div>
                <div className="text result">I don’t understand what you’re trying to say.</div>
              </div>

              <div className="example">
                <div className="label">Before</div>
                <div className="text">its look like the server not responding</div>
                <div className="label">After</div>
                <div className="text result">It looks like the server is not responding.</div>
              </div>

              <div className="example">
                <div className="label">Before</div>
                <div className="text">there is many reasons why this happening</div>
                <div className="label">After</div>
                <div className="text result">There are many reasons why this is happening.</div>
              </div>

              <div className="example">
                <div className="label">Before</div>
                <div className="text">she have been working here since two years</div>
                <div className="label">After</div>
                <div className="text result">She has been working here for two years.</div>
              </div>

              <div className="example">
                <div className="label">Before</div>
                <div className="text">please to contact me back asap</div>
                <div className="label">After</div>
                <div className="text result">Please contact me as soon as possible.</div>
              </div>
            </div>

            <p className="muted small" style={{marginTop:12}}>
              Tip: copy the "After" sentence to use immediately, or test alternative rewrites in the checker.
            </p>
          </section>

          {/* BEST PRACTICES */}
          <section className="scg-section">
            <h2>Best practices for better sentences</h2>
            <ol className="scg-bullets">
              <li>Keep sentences concise - split long sentences into two.</li>
              <li>Prefer active voice for clarity: "The team completed the project" vs "The project was completed by the team."</li>
              <li>Read sentences aloud - awkward phrasing becomes obvious.</li>
              <li>Use commas to separate clauses and prevent run-ons.</li>
              <li>When rewriting, decide tone first (formal vs casual).</li>
            </ol>
          </section>

          {/* CTA */}
          <section className="scg-section cta-strip">
            <h3>Fix your sentence now</h3>
            <p>Open the checker and paste a sentence - get corrections and rewrites instantly.</p>
            <div className="scg-ctas">
              <button className="btn btn-primary" onClick={goChecker}>Start Correcting with GrammAA</button>
              <button className="btn btn-ghost" onClick={goHome}>Back to Home</button>
            </div>
        
          </section>

          {/* FAQ */}
          <section className="scg-section faq">
            <h2>FAQ</h2>
            <div className="q">Is GrammAA free?</div>
            <div className="a">Yes - quick sentence checks are free. Create an account for unlimited checks and advanced rewrites.</div>

            <div className="q">Will the tool change my meaning?</div>
            <div className="a">No - GrammAA prioritizes preserving your meaning. Rewrites aim to keep intent while improving grammar and clarity.</div>

            <div className="q">Can it fix long paragraphs?</div>
            <div className="a">Yes - the checker handles long text and can produce sentence-level corrections or overall readability improvements.</div>
          </section>
        </article>

        {/* FOOTER LINKS */}
        <footer className="scg-footer glass-card">
          <div className="footer-left">
            <strong>More tools</strong>
            <div className="footer-links">
              <a onClick={() => navigate("/checker")}>Grammar Checker</a>
              <a onClick={() => navigate("/checker")}>Paragraph Corrector</a>
              <a onClick={() => navigate("/checker")}>Punctuation Checker</a>
              <a onClick={() => navigate("/checker")}>Spell Checker</a>
            </div>
          </div>

          <div className="footer-right muted">© {new Date().getFullYear()} GrammAA - All rights reserved.</div>
        </footer>
      </main>
    </div>
  );
};

export default SentenceCorrectorGuide;
